<template>
  <EditorTabFrame
    :loading="loading"
    :loaded="loaded"
    :load-error="loadError"
    :saving="saving"
    :dirty="isDirty"
    :conflict-message="conflictMessage"
    @save="save"
    @discard="discard"
    @reload="load"
  >
    <div v-if="form">
      <EditorSection title="Identificação" description="Nome, logo e período em que a campanha recebe doações.">
        <div class="ed-identity">
          <EditorImageField label="Logo da campanha" :current-path="logoPath" v-model="newLogo" />
          <div class="ed-grid ed-identity__fields">
            <div class="ed-span-all">
              <InputText
                v-model="form.name"
                type="text"
                place-holder="Nome"
                textLabel="Nome da campanha"
                invalidInputMessage="Preencha o nome da campanha"
                labelCharLimit="50"
              />
            </div>
            <div>
              <InputDateRange
                v-model="form.dates"
                textLabel="Vigência"
                place-holder="Início e fim da campanha"
                :monthsToShow="2"
                :required="false"
              />
            </div>
            <div>
            <InputSelect
              :items="campaignTypes"
              valueAttribute="id"
              textAttribute="name"
              :value="form.campaignTypeId"
              @input="form.campaignTypeId = toNumber($event)"
              textLabel="Tipo da campanha"
              helpLabel="Só pode mudar se a campanha não tiver projetos de outro tipo."
            />
            </div>
            <div v-if="form.campaignTypeId === CampaignTypes.Voluntariado" class="ed-span-all ed-radio-row">
              <span class="ed-label">Permitir que a mesma pessoa se inscreva mais de uma vez?</span>
              <v-radio-group v-model="form.subscribeMoreThanOneTime" row hide-details>
                <v-radio label="Sim" :value="true"></v-radio>
                <v-radio label="Não" :value="false"></v-radio>
              </v-radio-group>
            </div>
          </div>
        </div>
      </EditorSection>

      <EditorSection title="Valores" description="Limite mínimo de cada doação e a meta exibida na campanha.">
        <div class="ed-grid">
          <InputMoney
            v-model="form.minimumDonationValue"
            textLabel="Valor mínimo da doação"
            :precision="0"
            :required="false"
            :autoFocusOnValidationFail="false"
          />
          <InputMoney
            v-model="form.objetiveDonationValue"
            textLabel="Meta de captação"
            :precision="0"
            :required="false"
            :autoFocusOnValidationFail="false"
          />
        </div>
      </EditorSection>

      <EditorSection title="Acesso e login" description="Quem pode participar e como as pessoas entram na campanha.">
        <span class="ed-label">Quem pode participar</span>
        <v-radio-group v-model="form.isPublic" class="mt-0" hide-details>
          <v-radio label="Qualquer pessoa (campanha pública ou mista)" :value="true"></v-radio>
          <v-radio label="Somente membros da empresa ou entidade (campanha restrita)" :value="false"></v-radio>
        </v-radio-group>

        <div class="ed-subtitle mt-4">Login</div>
        <EditorSwitch v-model="form.withRecaptcha" label="Exigir reCAPTCHA no login" />
        <EditorSwitch v-model="form.onlySSO" label="Permitir apenas login por SSO" hint="Configure o SSO em Integrações." />
        <EditorSwitch v-model="form.useFixedMenu" label="Usar menu fixo na página da campanha" />

        <div class="ed-grid mt-3">
          <InputText
            type="number"
            v-model="form.loginAttempts"
            textLabel="Tentativas de login antes de bloquear"
            place-holder="Ex.: 5"
            helpLabel="Use 0 para não bloquear."
            :required="false"
          />
        </div>
      </EditorSection>
    </div>
  </EditorTabFrame>
</template>

<script>
import editorTab from "../editorTab.mixin";
import EditorTabFrame from "../EditorTabFrame.vue";
import EditorSection from "../EditorSection.vue";
import EditorSwitch from "../EditorSwitch.vue";
import EditorImageField from "../EditorImageField.vue";
import InputText from "@/components/input/InputText.vue";
import InputSelect from "@/components/input/InputSelect.vue";
import InputMoney from "@/components/input/InputMoney.vue";
import InputDateRange from "@/components/input/InputDateRange.vue";
import CampaignEditorService from "@/scripts/services/campaignEditor.service";
import { CampaignTypes, CampaignTypesList } from "@/scripts/models/enums/campaignTypes.enum";

const service = new CampaignEditorService();

export default {
  name: "GeneralTab",
  mixins: [editorTab],
  components: { EditorSection, EditorTabFrame, EditorSwitch, EditorImageField, InputText, InputSelect, InputMoney, InputDateRange },
  data() {
    return {
      CampaignTypes,
      campaignTypes: Object.freeze(CampaignTypesList),
      logoPath: null,
      newLogo: null,
    };
  },
  methods: {
    fetchSection() {
      return service.getGeneral(this.campaignId);
    },
    saveSection(body) {
      return service.saveGeneral(body);
    },
    toForm(dto) {
      return {
        name: dto.name || "",
        dates: [dto.initialDate, dto.finalDate],
        campaignTypeId: dto.campaignTypeId,
        subscribeMoreThanOneTime: !!dto.subscribeMoreThanOneTime,
        minimumDonationValue: dto.minimumDonationValue || 0,
        objetiveDonationValue: dto.objetiveDonationValue || 0,
        isPublic: !!dto.isPublic,
        loginAttempts: dto.loginAttempts || 0,
        withRecaptcha: !!dto.withRecaptcha,
        onlySSO: !!dto.onlySSO,
        useFixedMenu: !!dto.useFixedMenu,
      };
    },
    afterLoad(dto) {
      this.logoPath = dto.logo ? dto.logo.path : null;
      this.newLogo = null;
    },
    afterSave() {
      if (this.newLogo) this.logoPath = this.newLogo.dataUri;
      this.newLogo = null;
      this.$emit("header-changed", { name: this.form.name, campaignTypeId: this.form.campaignTypeId });
    },
    hasPendingFiles() {
      return !!this.newLogo;
    },
    validateForm() {
      if (!this.form.name || !this.form.name.trim()) {
        this.$store.commit("SET_SNACKBAR_MESSAGE", "Preencha o nome da campanha");
        return false;
      }
      return true;
    },
    buildPayload() {
      const dates = Array.isArray(this.form.dates) ? this.form.dates : [];
      return {
        name: this.form.name,
        initialDate: dates[0] || null,
        finalDate: dates[1] || null,
        campaignTypeId: this.form.campaignTypeId,
        subscribeMoreThanOneTime: this.form.subscribeMoreThanOneTime,
        minimumDonationValue: this.toNumber(this.form.minimumDonationValue),
        objetiveDonationValue: this.toNumber(this.form.objetiveDonationValue),
        isPublic: this.form.isPublic,
        loginAttempts: this.toNumber(this.form.loginAttempts) || 0,
        withRecaptcha: this.form.withRecaptcha,
        onlySSO: this.form.onlySSO,
        useFixedMenu: this.form.useFixedMenu,
        newLogo: this.newLogo,
      };
    },
    toNumber(value) {
      if (value === null || value === undefined || value === "") return null;
      const number = Number(value);
      return isNaN(number) ? null : number;
    },
  },
};
</script>
